import { useContext, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
} from "@bfc-software/app-toolbox";
import { useNavigate } from "@tanstack/react-router";
import { match } from "ts-pattern";
import { EmptyState, Icon } from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import {
  tiles,
  type Tile,
  type TileId,
} from "@bfc-software/bfc-web-ui/lib/tiles";
import { BrandedScreen } from "../../../-components/branded-screen";

import styles from "./index.module.css";

/**
 * The product picker - where a user lands after signing in, and the only screen
 * that knows about more than one product.
 *
 * It deliberately sits outside the app shell: no header, no navigation, no
 * warehouse. Each tile links to its product's own front door, and a product
 * that needs a warehouse asks for one there.
 */
export const Route = createFileRoute("/$lang/_layout/orgs/$org/_layout/")({
  component: RouteComponent,
});

/** The forklift mark used for Load Builder elsewhere - the Cloud Admin landing
 *  page at nextgen_config/templates/landing.gohtml - kept identical here.
 *
 * TODO: pallet has no surface for a non-feather custom mark (fill-based,
 * viewBox 0 0 489.903 489.903, versus feather's stroke-only 24-grid) - this
 * is a candidate for a "custom mark" surface in a later phase (phase 8). */
function ForkliftIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 489.903 489.903"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M469.2,261.652h-27.1v-219c0-11.5-9.4-20.9-20.9-20.9h-122c-74,0-134.5,60.5-134.5,134.5v105.3h-27.1 c-11.5,0-20.9,9.4-20.9,20.9v116.8c0,11.5,9.4,20.9,20.9,20.9h30c8.4,27.8,34.2,48,64.8,48c29.8,0,56.1-20.1,64.8-48h13.2 c8.4,27.8,34.2,48,64.8,48s56.4-20.1,64.8-48h29c11.5,0,20.9-8.3,20.9-19.8v-117.9C490.1,271.052,480.7,261.652,469.2,261.652z M299.3,62.552h101.1v199.1H235.7l37.5-37.5l7.3,7.3c13.3,11.8,25,4.2,29.2,0c8.3-8.3,8.3-20.9,0-29.2l-48-48 c-8.3-8.3-20.9-8.3-29.2,0s-8.3,20.9,0,29.2l11.6,11.5l-38.6,38.6v-77.1C205.5,104.252,248.2,62.552,299.3,62.552z M232.6,427.352 c-14.6,0-27.1-12.5-27.1-27.1s12.5-27.1,27.1-27.1s27.1,12.5,27.1,27.1S247.2,427.352,232.6,427.352z M375.4,427.352 c-14.6,0-27.1-12.5-27.1-27.1s12.5-27.1,27.1-27.1s27.1,12.5,27.1,27.1S390,427.352,375.4,427.352z M449.4,378.352h-9.9 c-9.1-26.7-34.3-45.9-64.2-45.9s-55.1,19.1-64.2,45.9h-14.5c-9.1-26.7-34.3-45.9-64.2-45.9s-55.1,19.1-64.2,45.9h-9.9v-77.1h290.9 v77.1H449.4z"></path>
      <path d="M65.8,80.252c-11.5,0-20.9,9.4-20.9,20.9v277.3h-24c-11.5,0-20.9,9.4-20.9,20.9s9.4,20.9,20.9,20.9h43.8 c11.5,0,20.9-8.3,21.9-19.8v-299.4C86.6,89.652,77.2,80.252,65.8,80.252z"></path>
    </svg>
  );
}

const TILE_ICONS: Record<TileId, ReactNode> = {
  outbound: <Icon name="Package" size={24} />,
  analytics: <Icon name="BarChart2" size={24} />,
  loadBuilder: <ForkliftIcon />,
  deliver: <Icon name="Truck" size={24} />,
  internal: <Icon name="Settings" size={24} />,
};

export function TileCard({
  tile,
  lang,
  org,
}: {
  tile: Tile;
  lang: string;
  org: string;
}) {
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  const { id } = tile;

  const body = (
    <>
      <span className={styles.tileIcon}>{TILE_ICONS[id]}</span>
      <span className={styles.tileTitle}>{t(`products.${id}.title`)}</span>
      <span className={styles.tileTagline}>{t(`products.${id}.tagline`)}</span>
      <span className={styles.tileDescription}>
        {t(`products.${id}.description`)}
      </span>
      <span className={styles.tileCta}>
        {match(tile.status)
          .with("open", () => (
            <>
              {t("tile.open")}
              <Icon name="ArrowRight" size={16} />
            </>
          ))
          .with("coming-soon", () => t("tile.comingSoon"))
          .exhaustive()}
      </span>
    </>
  );

  return match(tile.destination)
    .with({ kind: "external" }, ({ href }) => (
      // A product this app does not serve gets a plain anchor: a router link
      // would resolve against this app's route tree, and the destination is not
      // in it.
      <a
        href={href}
        target="_blank"
        rel="noopener"
        className={styles.tile}
        data-tile={id}
      >
        {body}
      </a>
    ))
    .with({ kind: "internal" }, ({ to }) => (
      // Every tile links to its product's own front door. Whether a product
      // needs a warehouse, and what to do when the organisation has none, is
      // the product's decision - the picker does not need to know.
      <Link
        to={to}
        params={{ lang, org }}
        className={styles.tile}
        data-tile={id}
      >
        {body}
      </Link>
    ))
    .exhaustive();
}

function RouteComponent() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const { state, logout } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  // Guarded by the parent layout route, which redirects to login otherwise -
  // this screen only ever renders once a session exists.
  if (state.status !== AUTHENTICATED) return null;

  // The org gate guarantees the URL org is the session org.
  const orgId = state.orgId;
  const orgName = AuthUtils.getOrgName(state, orgId);
  const orgSlug = AuthUtils.getOrgSlug(state, orgId);

  const visibleTiles = tiles({ orgSlug, orgId, token: state.token });

  return (
    <BrandedScreen>
      <h1 className={styles.heading}>{t("heading")}</h1>
      {orgName && (
        <p className={styles.company}>
          <Icon name="Home" size={16} />
          {orgName}
        </p>
      )}

      {visibleTiles.length === 0 ? (
        <div className={styles.empty}>
          <EmptyState heading={t("empty.heading")}>
            {t("empty.body")}
          </EmptyState>
        </div>
      ) : (
        <ul className={styles.tiles}>
          {visibleTiles.map((tile) => (
            <li key={tile.id} className={styles.tileItem}>
              <TileCard tile={tile} lang={params.lang} org={orgSlug} />
            </li>
          ))}
        </ul>
      )}

      {/* This screen has no shell header, so the way out lives here. */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.pill}
          onClick={() => {
            logout();
            navigate({
              to: "/$lang/auth/login",
              params: { lang: params.lang },
            });
          }}
        >
          <Icon name="LogOut" size={16} />
          {t("account.signOut")}
        </button>
      </div>
    </BrandedScreen>
  );
}
